import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SegmentedControl,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  applyTemplate,
  createRpSection,
  emptyRpLayout,
  PAGE_TEMPLATES,
  RP_SECTION_LIBRARY,
  rpSectionLibraryEntry,
  rpTemplate,
  type RpLayout,
  type RpPageTheme,
  type RpSection,
  type RpSectionGroup,
  type RpSectionType,
} from '@mlm/shared';
import {
  getReplicatedPageBlocks,
  listProducts,
  saveReplicatedPageLayout,
  type ReplicatedPage,
} from '../../lib/api';
import { RpEditorProvider } from './fields';
import { RpDeviceToggle, RpPreviewFrame, type RpDevice } from './preview-frame';
import { RpPreviewPane } from './preview-pane';
import { RpSectionInspector } from './section-inspector';
import { RpSectionOutline } from './section-outline';
import { RpThemePanel } from './theme-panel';

const GROUPS: { id: RpSectionGroup; label: string }[] = [
  { id: 'convert', label: 'Convert' },
  { id: 'persuade', label: 'Persuade' },
  { id: 'basic', label: 'Basic' },
];

/**
 * The visual page builder: outline → inspector → live preview.
 *
 * Replaces the pre-builder block editor, which offered five block types and one
 * styling knob (`align`) against a renderer hard-locked to a narrow centered
 * column. Sections here carry their own band, so `width: full` reaches the edge
 * of the screen — the thing that was structurally impossible before, at any
 * setting.
 */
export function RpPageEditor({
  page,
  pages,
  onPagePatch,
}: {
  page: ReplicatedPage;
  pages: ReplicatedPage[];
  /**
   * Page-row fields a content action needs to change (see `applyTemplateToPage`).
   * Awaitable so it can be sequenced INSIDE the save, not fired independently.
   */
  onPagePatch: (patch: Partial<ReplicatedPage>) => Promise<unknown>;
}) {
  const qc = useQueryClient();
  const contentQuery = useQuery({
    queryKey: ['admin-replicated-page-blocks', page.id],
    queryFn: () => getReplicatedPageBlocks(page.id),
  });

  const [layout, setLayout] = useState<RpLayout>(emptyRpLayout());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  // Page-row edits a template implies, applied together with the layout on save.
  const [pendingPagePatch, setPendingPagePatch] = useState<Partial<ReplicatedPage> | null>(null);
  const [addGroup, setAddGroup] = useState<RpSectionGroup>('convert');
  const [device, setDevice] = useState<RpDevice>('desktop');
  // Start optimistic: try the device frame, and drop to the in-page preview only
  // if it can't be reached. `frameReason` is null before any verdict, so the
  // fallback note doesn't flash during the handshake.
  const [frameOk, setFrameOk] = useState(true);
  const [frameReason, setFrameReason] = useState<string | null>(null);
  const handleFrameUnavailable = useCallback((reason: string) => {
    setFrameOk(false);
    setFrameReason(reason);
  }, []);

  // Reload when the server content arrives or the admin switches page.
  useEffect(() => {
    if (!contentQuery.data) return;
    setLayout(contentQuery.data.layout ?? emptyRpLayout());
    setSelectedId(contentQuery.data.layout?.sections[0]?.id ?? null);
    setPendingPagePatch(null);
    setDirty(false);
  }, [contentQuery.data]);

  const sections = layout.sections;
  const selected = sections.find((s) => s.id === selectedId) ?? null;

  // The page's tagged kits, so a `packages` section previews real cards rather
  // than a placeholder. Shares the products query the Packages card already uses.
  const kitsQuery = useQuery({
    queryKey: ['admin-products', 'kits'],
    queryFn: () => listProducts({ activeOnly: true, pageSize: 200 }),
  });
  const previewPackages = useMemo(() => {
    const byId = new Map((kitsQuery.data ?? []).map((p) => [p.id, p]));
    return (page.kitProductIds ?? [])
      .map((id) => byId.get(id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => ({ productId: p.id, name: p.name, price: p.price, pv: p.pv }));
  }, [kitsQuery.data, page.kitProductIds]);

  const editorCtx = useMemo(
    () => ({
      anchors: sections.map((s) => ({
        id: s.id,
        label: `${rpSectionLibraryEntry(s.type)?.label ?? s.type} · #${s.id}`,
      })),
      pages: pages.filter((p) => p.id !== page.id).map((p) => ({ slug: p.slug, title: p.title })),
      kits: (kitsQuery.data ?? []).filter((p) => p.isKit).map((p) => ({ id: p.id, name: p.name })),
      // Same query — it isn't filtered by `virtual`, so it already carries them.
      // A "Product" button sells the item itself, which only makes sense for a
      // service/digital good; a physical product still belongs on the storefront.
      virtualProducts: (kitsQuery.data ?? [])
        .filter((p) => p.isVirtual)
        .map((p) => ({ id: p.id, name: p.name })),
      // What the page row says this page sells, so a button still pointing at
      // sign-up can say so and offer to sell it instead. Resolved through the
      // same product list — an id we can't name is not worth surfacing.
      pageCtaProduct: (kitsQuery.data ?? [])
        .filter((p) => p.id === page.ctaProductId)
        .map((p) => ({ id: p.id, name: p.name }))[0],
    }),
    [sections, pages, page.id, page.ctaProductId, kitsQuery.data],
  );

  function mutate(next: RpSection[]) {
    setLayout((prev) => ({
      ...prev,
      // Any edit from the builder makes this a builder-authored page. A page
      // converted from legacy blocks stays `version: 0` (and keeps the historic
      // narrow shell) until the admin actually saves a change.
      version: 1,
      sections: next,
    }));
    setDirty(true);
  }

  function addSection(type: RpSectionType) {
    const s = createRpSection(
      type,
      sections.map((x) => x.id),
    );
    mutate([...sections, s]);
    setSelectedId(s.id);
  }

  function updateSection(next: RpSection) {
    mutate(sections.map((s) => (s.id === next.id ? next : s)));
  }

  function move(id: string, dir: -1 | 1) {
    const i = sections.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j]!, next[i]!];
    mutate(next);
  }

  function duplicate(id: string) {
    const i = sections.findIndex((s) => s.id === id);
    const src = sections[i];
    if (!src) return;
    // A fresh id, because ids are React keys AND `#anchor` targets.
    const copy = {
      ...JSON.parse(JSON.stringify(src)),
      id: createRpSection(
        src.type,
        sections.map((x) => x.id),
      ).id,
    } as RpSection;
    const next = [...sections];
    next.splice(i + 1, 0, copy);
    mutate(next);
    setSelectedId(copy.id);
  }

  /**
   * Replace the page with a template — never append. Splicing a template's hero
   * into the middle of an existing page produces something nobody designed, and
   * the confirm below makes the destructiveness explicit.
   */
  function applyTemplateToPage(templateId: string) {
    const t = rpTemplate(templateId);
    if (!t) return;
    if (
      sections.length > 0 &&
      !window.confirm(
        `Replace all ${sections.length} section${sections.length === 1 ? '' : 's'} with the “${t.label}” template?\n\nYour current sections are removed. Nothing changes on the live page until you save.`,
      )
    ) {
      return;
    }
    const next = applyTemplate(t);
    setLayout(next);
    setSelectedId(next.sections[0]?.id ?? null);
    setDirty(true);

    // Every template ends with its own call-to-action section (a guard test
    // enforces it), so leaving the automatic sign-up button on would stack two
    // buttons at the bottom of every templated page. STAGE that change — do not
    // write it now. Picking a template only builds a local draft; PATCHing the
    // page row here would push half the template live immediately (the live page
    // silently loses its sign-up button) while its content stayed a draft. It
    // goes to the server with the layout, in `save`.
    setPendingPagePatch({ ctaAuto: false, ...(t.page ?? {}) });
  }

  function setTheme(theme: RpPageTheme) {
    setLayout((prev) => ({ ...prev, version: 1, theme }));
    setDirty(true);
  }

  function toggle(id: string) {
    mutate(sections.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)));
  }

  function remove(id: string) {
    const s = sections.find((x) => x.id === id);
    const label = rpSectionLibraryEntry(s?.type ?? 'heading')?.label ?? 'section';
    if (!window.confirm(`Remove the ${label} section? This can't be undone once you save.`)) return;
    mutate(sections.filter((x) => x.id !== id));
    if (selectedId === id) setSelectedId(null);
  }

  const save = useMutation({
    meta: { successMessage: 'Page saved.' },
    mutationFn: async () => {
      const res = await saveReplicatedPageLayout(page.id, layout);
      // Layout first: if the page-row patch fails, the content is already safe
      // and the only casualty is a toggle the admin can flip by hand.
      if (pendingPagePatch) await onPagePatch(pendingPagePatch);
      return res;
    },
    onSuccess: (res) => {
      setLayout(res.layout);
      setPendingPagePatch(null);
      setDirty(false);
      qc.setQueryData(['admin-replicated-page-blocks', page.id], (prev: unknown) => ({
        ...(prev as object),
        layout: res.layout,
      }));
    },
  });

  // Warn before losing an unsaved draft to a tab close / reload.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  if (contentQuery.isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">Loading page…</CardContent>
      </Card>
    );
  }

  return (
    <RpEditorProvider value={editorCtx}>
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-base">
            Content — <span className="text-muted-foreground">{page.title}</span>
            {layout.version === 0 && sections.length > 0 ? (
              <span
                className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal uppercase text-muted-foreground"
                title="Built before the page builder. It renders exactly as it always has; saving converts it and unlocks full-width sections."
              >
                Classic layout
              </span>
            ) : null}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value="" onValueChange={applyTemplateToPage}>
              <SelectTrigger className="h-8 w-40">
                <SelectValue placeholder="Start from…" />
              </SelectTrigger>
              <SelectContent>
                {PAGE_TEMPLATES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dirty ? <span className="text-xs text-muted-foreground">Unsaved changes</span> : null}
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save page'}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[240px_340px_1fr]">
            {/* ── Outline ── */}
            <div className="space-y-2">
              <div className="space-y-1.5 rounded-md border p-2">
                <SegmentedControl
                  size="sm"
                  value={addGroup}
                  onChange={setAddGroup}
                  options={GROUPS.map((g) => ({ value: g.id, label: g.label }))}
                  ariaLabel="Section category"
                  className="w-full"
                />
                <Select value="" onValueChange={(v) => addSection(v as RpSectionType)}>
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="+ Add section" />
                  </SelectTrigger>
                  <SelectContent>
                    {RP_SECTION_LIBRARY.filter((e) => e.group === addGroup).map((e) => (
                      <SelectItem key={e.type} value={e.type}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <RpSectionOutline
                sections={sections}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onMove={move}
                onDuplicate={duplicate}
                onToggle={toggle}
                onRemove={remove}
              />
              <details className="rounded-md border">
                <summary className="cursor-pointer select-none px-2 py-1.5 text-xs font-semibold">
                  Page colours
                </summary>
                <div className="border-t p-2">
                  <RpThemePanel theme={layout.theme ?? {}} onChange={setTheme} />
                </div>
              </details>
            </div>

            {/* ── Inspector ── */}
            <div className="rounded-md border p-3">
              {selected ? (
                <RpSectionInspector section={selected} onChange={updateSection} />
              ) : (
                <p className="py-8 text-center text-xs text-muted-foreground">
                  {sections.length
                    ? 'Pick a section — in the list or straight from the preview.'
                    : 'Add a section to start.'}
                </p>
              )}
            </div>

            {/* ── Preview ── */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Preview</p>
                <p className="text-[11px] text-muted-foreground/70">click a section to edit it</p>
                {frameOk ? (
                  <div className="ml-auto">
                    <RpDeviceToggle value={device} onChange={setDevice} />
                  </div>
                ) : null}
              </div>
              {frameOk ? (
                <RpPreviewFrame
                  layout={layout}
                  device={device}
                  ctaLabel={page.ctaLabel || 'Sign up'}
                  ctaAuto={page.ctaAuto !== false}
                  packages={previewPackages}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  onUnavailable={handleFrameUnavailable}
                />
              ) : (
                <>
                  <RpPreviewPane
                    layout={layout}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    ctaLabel={page.ctaLabel || 'Sign up'}
                    ctaAuto={page.ctaAuto !== false}
                    packages={previewPackages}
                  />
                  {frameReason ? (
                    <p className="text-[11px] text-muted-foreground/80">
                      In-page preview ({frameReason}). Colours and phone widths won&apos;t match the
                      live page — set <span className="font-mono">VITE_MEMBER_URL</span> for the
                      device preview.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </RpEditorProvider>
  );
}
